import type { SetupNeed } from "@shared/connectors/setupNeed";
import { useSetupNeed } from "@/hooks/use-setup-need";
import { SkyButton, SkyCard, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import {
  MASKED_VALUE,
  MASK_STORED_AS_VALUE_SENTENCE,
  type DataflowDetail,
  type DataflowFieldInput,
  type LastValidation,
} from "@shared/types/dataflows";
import { CREDENTIAL_MISSING_CODE } from "@shared/connectors/credentialFields";
import {
  declaredTransportFields,
  transportSecurityState,
} from "@shared/connectors/transportSecurity";
import { encodeFieldValue, savedFieldValue } from "@/components/connections/fieldValues";
import { WITHHELD_REASON, WITHHELD_VALUE } from "@/components/connections/FieldGroups";
import { FieldRenderer } from "@/components/connections/FieldRenderer";
import { refusalPanelLines } from "@/components/connections/saveRefusal";
import type {
  ConnectorDefinition,
  FieldDefinition,
  FormValues,
} from "@/components/connections/types";
import { useFieldDependencies } from "@/components/connections/useFieldDependencies";
import { hasFieldErrors, useValidation } from "@/components/connections/useValidation";
import { McpInstanceSwitchNotice } from "@/components/mcp/McpInstanceSwitchNotice";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { runInstanceLink, withoutInstanceParam } from "@/components/runs/RunInstanceSwitchNotice";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { NOTHING_CHANGED_SENTENCE, NO_VERDICT_TITLE } from "@shared/connectors/noVerdict";
import { isReachFailure } from "@shared/connectors/reachDetails";
import { ReachDetails } from "@/components/connections/ReachDetails";
import { ValidationWait, removedSelectedSentence } from "@/components/connections/ValidationWait";
import { useConnectorDefinition } from "@/hooks/use-connectors";
import { useDataflow, useRevalidateDataflow, useUpdateDataflow } from "@/hooks/use-dataflows";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useMcpEnabled } from "@/hooks/use-mcp";
import { usePermissions } from "@/hooks/use-permissions";
import {
  VALIDATION_FAILED_CARD,
  joinParts,
  validatedAtSentence,
  validationSentence,
  type FieldLabel,
} from "@/lib/connections/validationText";
import { failureMessage } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { FORM_MAX_WIDTH } from "@/lib/layout";
import { ROUTES, absolutePath, connectionPath } from "@/lib/routes";
import {
  RecordNotFound,
  isMalformedRecordId,
  isNotFoundError,
} from "@/components/access/RecordNotFound";
import {
  pinnedFieldsChanged,
  pinnedFieldsOf,
  withPinnedValues,
} from "@/components/connections/pinnedFields";
import { PinnedField } from "@/components/connections/FieldGroups";

/** Said after a no-verdict Connect that saved what was typed first. */
const SAVED_NOT_TESTED =
  "What you typed is saved. It is not tested yet. Select Connect to try again.";

/**
 * Finish setting up a connection (ADR 0009 section 7, release 8B): `/connections/<id>/setup`.
 *
 * The owner's rule: no credential is ever sent by an AI app or returned to it. An AI app's
 * `create_connection` saves a connection as a draft with every field except its credentials
 * (the fields the connector catalog marks `isCredential`, and any field whose name looks like
 * one, such as a user name) and answers this page's address, `?instance=<id>` its only query
 * value. Here, signed in to skyData, the person sees the target the AI app set (host, database
 * and the like), types ONLY what is missing (the credentials and any required field the AI app
 * could not set) in skyData's own form and runs **Connect**: the typed values are saved first
 * (`PUT /dataflows/:id`), then the connection is tested with what is stored (`POST
 * /dataflows/:id/revalidate` with `fields: []`), so Ready (a successful test and a table selected)
 * always means the credential is in the vault. **Save** saves without a test.
 *
 * Nothing typed here goes anywhere but skyData's own API: the values are never logged, and the
 * AI app reads the connection back with credentials masked (`hasStoredSecret` only). A link that
 * names another instance waits for "Switch" (`?instance=` never selects), so the page never reads
 * the connection under the wrong instance.
 */
export default function ConnectionSetupPage({ id }: { id: string }) {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const { selectedInstance, instances, pendingInstanceId, selectInstance } = useTenantInstance();
  const link = runInstanceLink(
    pendingInstanceId,
    selectedInstance?.id ?? null,
    instances,
    "dataflows.read",
  );

  useEffect(() => {
    if (link.kind !== "same") return;
    navigate(withoutInstanceParam(location, search), { replace: true });
  }, [link.kind, location, search, navigate]);

  if (link.kind === "other" || link.kind === "no_access" || link.kind === "unresolved") {
    return (
      <div className="flex flex-col gap-4" data-testid="connection-setup-page">
        <McpInstanceSwitchNotice
          link={link}
          subject="This connection"
          onSwitch={selectInstance}
          testId="connection-setup-instance-switch"
        />
      </div>
    );
  }
  return <SetupScreen id={id} />;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Every field's value as the connection holds it now; a stored credential is the mask. */
function valuesFrom(definition: ConnectorDefinition, detail: DataflowDetail): FormValues {
  const byId = new Map(detail.fields.map((field) => [field.id, field]));
  const values: FormValues = {};
  for (const field of definition.fieldDefinitions) {
    const saved = byId.get(field.id);
    if (saved) {
      values[field.id] = savedFieldValue(saved, field.controlType);
    } else if (field.isCredential) {
      // Never a default for a credential: the person types it.
      values[field.id] = "";
    } else {
      values[field.id] =
        field.controlType === "checkbox" || field.controlType === "toggle"
          ? field.defaultValue === "true"
          : (field.defaultValue ?? "");
    }
  }
  return values;
}

/**
 * #1356 residual: the plain (not credential) fields whose stored value is the placeholder's text,
 * written before #1445. Every save refuses that value, so the person types the value itself here.
 * A stored credential is never one of them: it reads back as the placeholder and stays write-only.
 */
function placeholderValueIds(detail: DataflowDetail | undefined): Set<string> {
  return new Set(
    (detail?.fields ?? [])
      .filter(
        (field) =>
          !field.isCredential &&
          !field.hasStoredSecret &&
          !field.withheld &&
          field.value === MASKED_VALUE,
      )
      .map((field) => field.id),
  );
}

/** A target field's value as read-only text: what the AI app set, for the person to check. */
function targetText(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((item) => JSON.stringify(item)).join(", ");
  return String(value);
}

/** The values the form was seeded from: what "typed here" is measured against. */
interface Seeded {
  values: FormValues;
}

/** An AI app's draft waits for the person to check its destination before a credential is typed. */
function confirmationOf(
  aiDraft: string | null,
  current: DataflowDetail,
  destinationChecked: boolean,
) {
  const needsConfirm = aiDraft !== null && !current.destinationConfirmedAt;
  return {
    needsConfirm,
    blockedByConfirm: needsConfirm && !destinationChecked,
    confirmBody: needsConfirm && destinationChecked ? { confirmDestination: true } : {},
  };
}

/**
 * " · Setup incomplete", " · Ready" or " · Disabled", after the connector's name: the Connections
 * list's words for a draft (owner, 2026-09-28: "Draft is ours; Setup incomplete is what the reader
 * needs to do"), never the stored state's (#993 review). This page holds a draft or a connection
 * just saved from it, which the list reads as Ready until its first import.
 */
function stateSuffix(state: DataflowDetail["state"]): string {
  if (state === "draft") return " · Setup incomplete";
  return state === "ready" ? " · Ready" : " · Disabled";
}

/** What the AI app set, and how the credential travels, for the person to check first. */
function TargetBlock({
  targetFields,
  transportFields,
  aiDraft,
  values,
  connectionId,
  withheld,
}: {
  targetFields: FieldDefinition[];
  transportFields: ReturnType<typeof declaredTransportFields>;
  aiDraft: string | null;
  values: FormValues;
  connectionId: string;
  /** #1107: the fields this reader may not see; each reads Hidden, never empty or Not set. */
  withheld: ReadonlySet<string>;
}) {
  const anyWithheld =
    targetFields.some((field) => withheld.has(field.id)) ||
    (aiDraft !== null && transportFields.some((entry) => withheld.has(entry.id)));
  return (
    <>
      <SkyText as="h3" size={300} weight="semibold" block>
        Where it connects
      </SkyText>
      <SkyText as="p" size={200} color="muted" block>
        Check these before you type a credential: it will be used against this target.
      </SkyText>
      <dl
        className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1"
        data-testid="connection-setup-target"
      >
        {targetFields.map((field) => (
          <div key={field.id} className="contents">
            <dt>
              <SkyText size={300} weight="semibold">
                {field.name}
              </SkyText>
            </dt>
            <dd
              className="min-w-0 [overflow-wrap:anywhere]"
              data-testid={`connection-setup-target-${field.id}`}
            >
              <SkyText size={300}>
                {withheld.has(field.id) ? WITHHELD_VALUE : targetText(values[field.id])}
              </SkyText>
            </dd>
          </div>
        ))}
      </dl>
      {aiDraft !== null && transportFields.length > 0 ? (
        <>
          <SkyText as="h3" size={300} weight="semibold" block>
            How it connects
          </SkyText>
          <dl
            className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1"
            data-testid="connection-setup-transport"
          >
            {transportFields.map((entry) => {
              const state = withheld.has(entry.id)
                ? { text: WITHHELD_VALUE, weak: false }
                : transportSecurityState(entry, values[entry.id]);
              return (
                <div key={entry.id} className="contents">
                  <dt>
                    <SkyText size={300} weight="semibold">
                      {entry.label}
                    </SkyText>
                  </dt>
                  <dd data-testid={`connection-setup-transport-${entry.id}`}>
                    <SkyText size={300} color={state.weak ? "danger" : undefined}>
                      {state.text}
                    </SkyText>
                  </dd>
                </div>
              );
            })}
          </dl>
        </>
      ) : null}
      {anyWithheld ? (
        <SkyText as="p" size={200} color="muted" block testId="connection-setup-withheld">
          {WITHHELD_REASON}
        </SkyText>
      ) : null}
      <SkyText as="p" size={200} color="muted" block>
        Something wrong? Change it in the{" "}
        <Link
          href={absolutePath(connectionPath(connectionId))}
          className="text-accent-text underline"
          data-testid="connection-setup-editor-link"
        >
          connection editor
        </Link>{" "}
        instead.
      </SkyText>
    </>
  );
}

/** The last connection test's answer. */
function ValidationBar({
  validation,
  connected,
  fields,
}: {
  validation: LastValidation;
  connected: boolean;
  fields: readonly FieldLabel[];
}) {
  // #1220: the page reopens from the stored answer, so it says when that answer was given.
  const zone = useDisplayTz();
  // PR #1312 review (#1267): a credential is missing. No test failed, so the bar says what is
  // missing in the server's words, as the editor's does.
  if (validation.status === "failed" && validation.errorCode === CREDENTIAL_MISSING_CODE) {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId="connection-setup-credential-missing"
        title="A credential is missing"
        message={validation.message}
      />
    );
  }
  return (
    <SkyMessageBar
      intent={connected ? "success" : "error"}
      layout="multiline"
      testId="connection-setup-validation"
      title={connected ? "Connected" : "The connection test failed"}
      message={joinParts(
        validatedAtSentence(
          validation.at,
          zone,
          connected ? "Last validated" : "Last validation failed",
        ),
        // The hub names the missing keys; the page names them as its form does, as the editor's does.
        validationSentence(validation.message, fields),
      )}
    />
  );
}

function SetupScreen({ id }: { id: string }) {
  const { can } = usePermissions();
  const zone = useDisplayTz();
  const canSave = can("dataflows.write");
  // #1812: AI apps off, the intro does not speak of them. A draft one made earlier still finishes
  // here, and its own "drafted by an AI app" warning stays (a record of where it came from).
  const mcpEnabled = useMcpEnabled();
  const canConnect = can("dataflows.validate");
  // #1652: an id that is not a uuid is never asked for; the page reads not found.
  const idMalformed = isMalformedRecordId(id);
  const detailQuery = useDataflow(idMalformed ? null : id);
  const detail = detailQuery.data;
  const definitionQuery = useConnectorDefinition(detail?.connectorName);
  const definition = definitionQuery.data;

  const [seeded, setSeeded] = useState<Seeded | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<string | null>(null);
  const [validation, setValidation] = useState<LastValidation | null>(null);
  const [noVerdict, setNoVerdict] = useState<string | null>(null);
  // #2485: the "could not reach" card's details, from the answer and the form as it was tested.
  const [reach, setReach] = useState<{
    at: string;
    reference: string | null;
    values: FormValues;
  } | null>(null);
  const [removedLine, setRemovedLine] = useState<string | null>(null);
  const [saved, setSaved] = useState<DataflowDetail | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  // F3: a connection an AI app drafted may name any host, so a credential is typed only after the
  // person has checked the target below and ticked this; the server refuses it otherwise.
  const [destinationChecked, setDestinationChecked] = useState(false);
  const formError = useInlineError();
  // #1099 (PR #1276 review): the panel's detail lines in on-screen labels, as the editor's are;
  // never the body's keys (`fields.<id>`, `confirmDestination`) or a stale write's stamp.
  const [refusalLines, setRefusalLines] = useState<string[]>([]);
  const revalidate = useRevalidateDataflow(id);
  const update = useUpdateDataflow(id);

  // Seeded once, from the first detail and catalog entry; a refetch after Connect or Save never
  // overwrites what the person is typing (each write reseeds from its own answer below).
  if (definition && detail && seeded === null) {
    // #1932: a field the environment pins is filled in from the pin, read-only below. Review of
    // #2429: `seeded` stays what is stored, so a stored address that is not the pin is a change
    // Connect and Save write, and "Where it connects" never shows the pin as stored.
    const stored = valuesFrom(definition, detail);
    setSeeded({ values: stored });
    setValues(withPinnedValues(stored, pinnedFieldsOf(definition)));
    setLoadedUpdatedAt(detail.updatedAt);
    setValidation(detail.lastValidation);
  }

  const aiDraft = (saved ?? detail)?.mcpDraftedBy ?? null;

  const fieldDefinitions = useMemo(
    () => (definition?.fieldDefinitions ?? []).filter((field) => !field.isPlatformManaged),
    [definition],
  );
  const fieldState = useFieldDependencies(fieldDefinitions, values);
  const errors = useValidation(fieldDefinitions, values, fieldState);

  const isVisible = (field: FieldDefinition) => fieldState[field.id]?.visible ?? field.visible;
  // #1107: below `dataflows.write` the server withholds every set value but a known setting. A
  // withheld field holds a value, so it is never "missing", and it reads Hidden.
  const withheldIds = new Set(
    (detail?.fields ?? []).filter((field) => field.withheld).map((field) => field.id),
  );
  const isRequired = (field: FieldDefinition) =>
    fieldState[field.id]?.required ?? field.validation.required?.value ?? false;
  // F4: the server does not refuse an edit to a field the connector or a rule disables, so a
  // disabled field is never offered here, even while it holds the placeholder.
  const placeholderIds = new Set(
    [...placeholderValueIds(saved ?? detail)].filter((id) => {
      const field = fieldDefinitions.find((candidate) => candidate.id === id);
      return field !== undefined && !(fieldState[id]?.disabled ?? field.disabled);
    }),
  );

  // #1932 review of #2429: a pinned field whose stored value is not the pin, listed below
  // read-only so Connect and Save write it.
  const pinnedFields = pinnedFieldsOf(definition);
  const pinnedToSave = seeded ? pinnedFieldsChanged(seeded.values, pinnedFields) : new Set();
  // What the person fills here: every credential, and any required field the connection does not
  // hold yet (an AI app may not set a field whose name looks like a credential, such as a user
  // name, so it arrives empty).
  const setupFields = seeded
    ? fieldDefinitions
        // A field hidden by its dependencies that still holds the placeholder is listed anyway:
        // every save refuses that value, so the person must be able to replace it.
        .filter((field) => isVisible(field) || placeholderIds.has(field.id))
        .filter(
          (field) =>
            field.isCredential ||
            pinnedToSave.has(field.id) ||
            placeholderIds.has(field.id) ||
            (isRequired(field) &&
              !withheldIds.has(field.id) &&
              isEmptyValue(seeded.values[field.id])) ||
            // An AI app never pins a host key: with strict checking on and no pin, the person
            // pastes the fingerprint here.
            (field.id === "hostKeyFingerprint" &&
              aiDraft !== null &&
              seeded.values.strictHostKeyChecking === true &&
              !withheldIds.has(field.id) &&
              isEmptyValue(seeded.values[field.id])),
        )
        .sort((a, b) => a.order - b.order)
    : [];
  const setupIds = new Set(setupFields.map((field) => field.id));
  // The settings that decide whether the credential travels over a checked channel (certificate,
  // host key): listed apart, in the block the tick covers.
  const transportFields = declaredTransportFields(definition?.fieldDefinitions);
  const transportIds = new Set(transportFields.map((field) => field.id));
  // What the AI app set, shown so the person checks where the credential will be used first.
  const targetFields = seeded
    ? fieldDefinitions
        .filter(isVisible)
        .filter(
          (field) => !setupIds.has(field.id) && !field.isCredential && !transportIds.has(field.id),
        )
        .filter((field) => withheldIds.has(field.id) || !isEmptyValue(seeded.values[field.id]))
        .sort((a, b) => a.order - b.order)
    : [];

  if (idMalformed || isNotFoundError(detailQuery.error)) {
    return (
      <RecordNotFound
        noun="connection"
        listLabel="Back to Connections"
        listPath={ROUTES.connections}
        testId="connection-not-found"
      />
    );
  }
  if (detailQuery.isError || definitionQuery.isError) {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        testId="connection-setup-load-error"
        title="We could not load this connection"
        message={failureMessage(
          detailQuery.error ?? definitionQuery.error,
          "Reload the page to try again.",
        )}
      />
    );
  }
  if (!detail || !definition || !seeded) {
    return (
      <SkyText size={300} color="muted" testId="connection-setup-loading">
        Loading the connection…
      </SkyText>
    );
  }

  /** Only what the person typed here: the fields whose value moved from what was seeded. */
  const typedInputs = (): DataflowFieldInput[] =>
    setupFields
      .filter(
        (field) => JSON.stringify(values[field.id]) !== JSON.stringify(seeded.values[field.id]),
      )
      .map((field) => ({ id: field.id, value: encodeFieldValue(values[field.id]) }));

  /** Every field, as a save replaces the field set: an untouched stored credential stays the mask. */
  const allInputs = (): DataflowFieldInput[] =>
    fieldDefinitions.map((field) => ({ id: field.id, value: encodeFieldValue(values[field.id]) }));

  /** A refused save or test, with its details named by the labels on this page. */
  const showRefusal = (error: unknown, fallback: string, sent: readonly DataflowFieldInput[]) => {
    setRefusalLines(
      refusalPanelLines(error, {
        fieldDefinitions,
        entitySingular: definition.entitySelection?.entityLabelSingular ?? "entity",
        entityPlural: definition.entitySelection?.entityLabelPlural ?? "entities",
        sent: { fieldIds: sent.map((field) => field.id) },
      }),
    );
    formError.show(error, fallback);
  };

  // #1356: a plain value still holding the placeholder is refused by every save, so the page says
  // so beside the field and sends nothing until the person types the value itself (or clears it).
  const placeholderErrors = Object.fromEntries(
    setupFields
      .filter((field) => placeholderIds.has(field.id) && values[field.id] === MASKED_VALUE)
      .map((field) => [field.id, MASK_STORED_AS_VALUE_SENTENCE]),
  );
  const hasPlaceholder = Object.keys(placeholderErrors).length > 0;
  const setupErrors: Record<string, string | null> = Object.fromEntries(
    setupFields.map((field) => [field.id, placeholderErrors[field.id] ?? errors[field.id] ?? null]),
  );
  const current = saved ?? detail;
  const { needsConfirm, blockedByConfirm, confirmBody } = confirmationOf(
    aiDraft,
    current,
    destinationChecked,
  );
  // A target field the AI app left unset or as the placeholder is typed in this form, not shown above.
  const targetEntered = setupFields.some((field) => field.isTarget && !field.isCredential);
  const busy = revalidate.isPending || update.isPending;
  const connected = validation?.status === "success";
  // #2439 review: a stored failed test (not a missing credential), which a no-verdict card folds in.
  const storedFailed =
    validation?.status === "failed" && validation.errorCode !== CREDENTIAL_MISSING_CODE
      ? validation
      : null;

  /**
   * Connect stores what was typed, then tests what is stored (`fields: []`). A test of typed
   * values alone would record a success the server reads as Ready while the vault holds nothing,
   * so a person who closed the page before Save would leave a "Ready" connection whose runs fail.
   * This way Ready always means stored and tested; Save stays for saving without a test.
   */
  async function onConnect() {
    setShowErrors(true);
    formError.clear();
    setNoVerdict(null);
    setReach(null);
    setRemovedLine(null);
    if (hasFieldErrors(setupErrors) || !definition || blockedByConfirm) return;
    let stamp = loadedUpdatedAt;
    let savedFirst = false;
    // An unconfirmed AI draft saves first even with nothing typed: the save carries the tick the
    // test needs when a credential is already stored.
    if (typedInputs().length > 0 || confirmBody.confirmDestination) {
      let after: DataflowDetail;
      const sent = allInputs();
      try {
        after = await update.mutateAsync({
          fields: sent,
          expectedUpdatedAt: stamp ?? undefined,
          ...confirmBody,
        });
      } catch (error) {
        showRefusal(error, "We could not save this connection.", sent);
        return;
      }
      const next = valuesFrom(definition, after);
      setSeeded({ values: next });
      setValues(withPinnedValues(next, pinnedFieldsOf(definition)));
      stamp = after.updatedAt;
      setLoadedUpdatedAt(stamp);
      setSaved(after);
      savedFirst = true;
    }
    try {
      const result = await revalidate.mutateAsync({ fields: [] });
      setRemovedLine(
        result.validation.removedSelected
          ? removedSelectedSentence(result.validation.removedSelected, {
              singular: (definition.entitySelection?.entityLabelSingular ?? "entity").toLowerCase(),
              plural: (definition.entitySelection?.entityLabelPlural ?? "entities").toLowerCase(),
            })
          : null,
      );
      // SD2/SD3: the source gave no verdict. Nothing was recorded, so the page keeps what it
      // showed and says so, without the failed-test bar.
      if (result.recorded === false && (result.noVerdict || result.validation.noVerdict)) {
        // Connect saved what was typed first, so "nothing was changed" would not be true.
        const sentence = result.validation.message;
        setNoVerdict(
          savedFirst
            ? `${validationSentence(
                sentence.replace(NOTHING_CHANGED_SENTENCE, "").trim(),
                [],
              )} ${SAVED_NOT_TESTED}`
            : validationSentence(sentence, []),
        );
        setReach(
          isReachFailure(definition.name, result.validation.errorCode)
            ? { at: result.validation.at, reference: result.referenceId ?? null, values }
            : null,
        );
        setLoadedUpdatedAt(result.dataflow.updatedAt);
        setSaved(result.dataflow);
        return;
      }
      setNoVerdict(null);
      setReach(null);
      setValidation({
        status: result.validation.status,
        message: result.validation.message,
        errorCode: result.validation.errorCode,
        at: result.validation.at,
        by: null,
      });
      setLoadedUpdatedAt(result.dataflow.updatedAt);
      setSaved(result.dataflow);
    } catch (error) {
      showRefusal(error, "We could not test this connection.", []);
    }
  }

  function onSave() {
    setShowErrors(true);
    formError.clear();
    if (!definition || blockedByConfirm || hasPlaceholder) return;
    const sent = allInputs();
    update.mutate(
      { fields: sent, expectedUpdatedAt: loadedUpdatedAt ?? undefined, ...confirmBody },
      {
        onSuccess: (after) => {
          // The typed values are stored now; the form holds the masks from here on.
          const next = valuesFrom(definition, after);
          setSeeded({ values: next });
          setValues(withPinnedValues(next, pinnedFieldsOf(definition)));
          setLoadedUpdatedAt(after.updatedAt);
          setSaved(after);
        },
        onError: (error) => showRefusal(error, "We could not save this connection.", sent),
      },
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${FORM_MAX_WIDTH}`} data-testid="connection-setup-page">
      <SkyMessageBar
        intent="info"
        layout="multiline"
        testId="connection-setup-intro"
        title="Enter the credentials here, in skyData"
        message={`${mcpEnabled ? "AI apps never send or receive credentials. " : ""}This connection was saved as a draft without ${mcpEnabled ? "them" : "its credentials"}. ${canSave ? "Type them below and select Connect: skyData saves what you typed and tests the connection with it. " : "Someone who can change connections types them here and selects Connect. "}What you type here stays in skyData.${mcpEnabled ? " An AI app only ever sees that a credential is stored." : ""}`}
      />

      {needsConfirm ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="connection-setup-ai-draft"
          title={`This connection was drafted by an AI app (${aiDraft})`}
          message="Check where your credential will be sent:"
        />
      ) : null}

      <SkyCard className="flex flex-col gap-3 p-4">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block testId="connection-setup-name">
            {detail.displayName}
          </SkyText>
          <SkyText size={200} color="muted" block testId="connection-setup-connector">
            {definition.displayName}
            {stateSuffix(detail.state)}
          </SkyText>
        </div>
        {targetFields.length > 0 ? (
          <TargetBlock
            targetFields={targetFields}
            transportFields={transportFields}
            aiDraft={aiDraft}
            values={seeded.values}
            connectionId={detail.id}
            withheld={withheldIds}
          />
        ) : null}
      </SkyCard>

      <SkyCard className="flex flex-col gap-4 p-4" testId="connection-setup-form">
        <SkyText as="h3" size={300} weight="semibold" block>
          What you enter
        </SkyText>
        {setupFields.length === 0 ? (
          <SkyText as="p" size={300} block testId="connection-setup-nothing">
            {canSave
              ? "Nothing is missing. Select Connect to test the connection with what is stored."
              : "Nothing is missing."}
          </SkyText>
        ) : (
          setupFields.map((field) =>
            pinnedFields.has(field.id) ? (
              <PinnedField
                key={field.id}
                field={field}
                value={pinnedFields.get(field.id) ?? ""}
                changed
              />
            ) : (
              <FieldRenderer
                key={field.id}
                field={field}
                value={values[field.id]}
                // A reader who cannot change the connection is not told to enter a value; the page's
                // read-only line says who can.
                error={
                  canSave
                    ? ((showErrors ? setupErrors[field.id] : placeholderErrors[field.id]) ?? null)
                    : null
                }
                disabled={!canSave || busy || (fieldState[field.id]?.disabled ?? field.disabled)}
                readOnly={!canSave}
                required={isRequired(field)}
                onChange={(fieldId, value) =>
                  setValues((previous) => ({ ...previous, [fieldId]: value }))
                }
              />
            ),
          )
        )}

        <ValidationWait
          active={revalidate.isPending}
          mode="validate"
          testId="connection-setup-wait"
        />
        {/* Always mounted, so the answer below is announced when it arrives. */}
        <div role="status" aria-live="polite" data-testid="connection-setup-answer">
          {/* #2439: an error card the person cannot miss, worded by cause, never "Validation
              failed" (SD2: nothing about the values was judged). */}
          {noVerdict ? (
            <SkyMessageBar
              intent="error"
              layout="multiline"
              testId="connection-setup-no-verdict"
              className={VALIDATION_FAILED_CARD}
              title={NO_VERDICT_TITLE}
              message={
                <>
                  {joinParts(
                    noVerdict,
                    storedFailed
                      ? validatedAtSentence(storedFailed.at, zone, "Last validation failed")
                      : "",
                    storedFailed
                      ? validationSentence(storedFailed.message, definition?.fieldDefinitions ?? [])
                      : "",
                  )}
                  {reach ? (
                    <ReachDetails
                      values={reach.values}
                      reference={reach.reference}
                      at={reach.at}
                      zone={zone}
                      testId="connection-setup-no-verdict-details"
                    />
                  ) : null}
                </>
              }
            />
          ) : null}
          {removedLine ? (
            <SkyText as="p" size={300} block testId="connection-setup-removed-selected">
              {removedLine}
            </SkyText>
          ) : null}
        </div>
        {/* #2439 review: one red card, never two. While the no-verdict card shows, the stored
            failed test is its last line instead. */}
        {validation && !(noVerdict && storedFailed) ? (
          <ValidationBar
            validation={validation}
            connected={connected}
            fields={definition?.fieldDefinitions ?? []}
          />
        ) : null}

        <InlineErrorBar
          error={formError.error ? { ...formError.error, details: refusalLines } : null}
          title="That did not work"
          onDismiss={formError.clear}
          testId="connection-setup-error"
        />

        {!canSave ? (
          <SkyText as="p" size={300} block testId="connection-setup-read-only">
            You can see this connection but not change it. Ask someone who can change connections in
            this instance to finish its setup.
          </SkyText>
        ) : null}

        {needsConfirm && canSave ? (
          <SingleCheckbox
            label="I checked the destination"
            description={
              targetFields.length > 0
                ? targetEntered
                  ? "The credential you type is sent to the target shown above and the target you type in this form. Connect and Save wait for this."
                  : "The credential you type is sent to the target shown above. Connect and Save wait for this."
                : targetEntered
                  ? "The credential you type is sent to the target you type in this form. Connect and Save wait for this."
                  : "This connection names no target to check. Tick this to confirm it. Connect and Save wait for this."
            }
            checked={destinationChecked}
            onChange={setDestinationChecked}
            disabled={busy}
            testId="connection-setup-confirm-destination"
          />
        ) : null}

        {canSave ? (
          <div className="flex flex-wrap gap-2">
            <SkyButton
              appearance={connected ? "secondary" : "primary"}
              testId="connection-setup-connect"
              loading={revalidate.isPending}
              disabled={busy || !canConnect || !canSave || blockedByConfirm || hasPlaceholder}
              onClick={() => void onConnect()}
            >
              Connect
            </SkyButton>
            <SkyButton
              appearance={connected ? "primary" : "secondary"}
              testId="connection-setup-save"
              loading={update.isPending}
              disabled={busy || !canSave || blockedByConfirm || hasPlaceholder}
              onClick={onSave}
            >
              Save
            </SkyButton>
          </div>
        ) : null}
      </SkyCard>

      {saved ? <SavedNotice saved={saved} /> : null}
    </div>
  );
}

/** What a save left the connection as, and the next step. */
/** #2481: what a saved draft still needs, in the connector's words (a report, not a table). */
export function savedMessage(validated: boolean, need: SetupNeed): string {
  const stored = "Its credentials are stored.";
  if (need === "validate") {
    return validated
      ? `${stored} Open the connection to finish its setup.`
      : `${stored} Select Connect until the test succeeds, and it becomes ready.`;
  }
  const reports = need === "report";
  if (validated) {
    const next = reports
      ? "Add at least one report to import"
      : "Choose at least one table to import";
    return `${stored} ${next}, and it becomes ready.`;
  }
  const chosen = reports ? "a report added" : "a table chosen";
  return `${stored} Select Connect until the test succeeds. With ${chosen}, it becomes ready.`;
}

export function chooseLabel(validated: boolean, need: SetupNeed): string {
  if (!validated || need === "validate") return "Open the connection";
  return need === "table" ? "Choose tables" : "Add a report";
}

function SavedNotice({ saved }: { saved: DataflowDetail }) {
  const need = useSetupNeed(saved.connectorName, saved.selectedCount);
  const editor = absolutePath(connectionPath(saved.id));
  if (saved.state === "ready") {
    return (
      <SkyMessageBar
        intent="success"
        layout="multiline"
        testId="connection-setup-saved"
        title={`${saved.displayName} is ready`}
        message="Its credentials are stored in skyData. It can now be scheduled and run."
        actions={
          <Link
            href={editor}
            className="text-sm text-accent-text underline"
            data-testid="connection-setup-open"
          >
            Open the connection
          </Link>
        }
      />
    );
  }
  const validated = saved.lastValidation?.status === "success";
  const credentialMissing = saved.lastValidation?.errorCode === CREDENTIAL_MISSING_CODE;
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      testId="connection-setup-saved"
      title="Saved. It is still a draft"
      message={
        credentialMissing ? (saved.lastValidation?.message ?? "") : savedMessage(validated, need)
      }
      actions={
        <Link
          href={editor}
          className="text-sm text-accent-text underline"
          data-testid="connection-setup-open"
        >
          {chooseLabel(validated, need)}
        </Link>
      }
    />
  );
}
